// ============================================
// src/App/autoReadPullMerge.js
// MA-792 — pull-side decision + write/retry logic for the parent's
// auto-read toggle (students.auto_read_enabled).
//
// THE BUG: ParentSettingsSections saved the toggle locally, and on a failed
// cloud write only toasted "It'll sync next time" — nothing retried, and
// App.jsx's cloud merge (`cloudStudent.auto_read_enabled ?? prev.autoReadEnabled`)
// restored the OLD cloud value on the next fresh load. The cloud has no
// updated-at for this field (no new column, by design), so the device keeps
// a LOCAL flag, `progress.autoReadPendingSync`, set in the same write as
// `autoReadEnabled` and cleared only once the cloud has the value.
//
//   pending=true  -> the local value is unsynced: keep it, push it
//   pending=false -> cloud wins (cross-device hydration), flag stays false
//
// Plain JS, no React, no DOM: everything is injectable and unit-testable.
//
// CHILD-SCOPED COMPLETION (RED M1): App.jsx's setProgress closes over the
// activeChildId of the render that created it and persists under that key.
// A write that completes after a child switch must therefore NEVER touch a
// captured setter. Every completion goes through applyToChild(): the LIVE
// setter when that same child is still active, otherwise a direct
// load -> transform -> save of THAT child's stored progress.
//
// KNOWN, ACCEPTED SEMANTICS
//  - "Last device to reconnect wins" (RED S4): with no stamp column, a device
//    that toggled offline overrides a newer value another device wrote once
//    it reconnects. Operator decision, logged on the item.
//  - App.jsx reads pendingAutoReadPushRef right after the setProgress updater
//    (RED S7): a pre-existing pattern shared with the levels/assigned/
//    suppressed pushes (MA-375/MA-395); not changed here.
//  - Kiosk-claimed children (supabaseStudentId null, writes under
//    kioskClaimedStudentId) never pass isChildLinkedFor, so their reconnect
//    retry never fires: such a write targets a row the signed-in teacher does
//    not own. The next load still retries/clears normally.
//  - isChildLinkedFor reads the last render's registry; for one tick after a
//    child removal it can still list that child (RED R3-N4, accepted).
//  - WIRING IS NOT RENDER-TESTED: App.jsx and ParentSettingsSections.tsx call
//    into this module, pinned only by text pins (cloudMergeGuard.test.mjs for
//    App.jsx, levelsDeviceHop.test.mjs for Settings) that prove a line exists.
// ============================================

/** Flag-clearing is only legitimate when the stored value is the one sent. */
const clearIfCurrent = (sent) => (prev) =>
  prev && prev.autoReadEnabled === sent && prev.autoReadPendingSync
    ? { ...prev, autoReadPendingSync: false }
    : prev;

/** A stale success may have landed AFTER a newer write: re-arm so the next
 *  load re-pushes the local value (RED S5). */
const rearm = () => (prev) =>
  prev && typeof prev.autoReadEnabled === "boolean" && !prev.autoReadPendingSync
    ? { ...prev, autoReadPendingSync: true }
    : prev;

/**
 * @param {{ cloudValue: boolean|null|undefined, prevValue: boolean|null|undefined, pending: boolean|null|undefined }} args
 * @returns {{ value: boolean|null|undefined, pending: boolean, needsPush: boolean }}
 */
export function resolveAutoReadPullMerge({ cloudValue, prevValue, pending }) {
  if (pending === true && typeof prevValue === "boolean") {
    // The cloud already has it: drop the flag, nothing to send (RED S2a).
    if (cloudValue === prevValue) return { value: prevValue, pending: false, needsPush: false };
    return { value: prevValue, pending: true, needsPush: true };
  }
  // Hydration from the cloud always clears a stale/meaningless flag.
  return { value: cloudValue ?? prevValue, pending: false, needsPush: false };
}

/**
 * The whole auto-read slice of App.jsx's cloud->local merge. App.jsx spreads
 * `patch` into the progress it returns and hands `push` to the dispatcher.
 * @returns {{ patch: { autoReadEnabled: any, autoReadPendingSync: boolean },
 *             push: { studentId: string, localChildId: string|null, value: boolean } | null }}
 */
export function planAutoReadOnMerge({ prev, cloudStudent, localChildId }) {
  const m = resolveAutoReadPullMerge({
    cloudValue: cloudStudent?.auto_read_enabled,
    prevValue: prev?.autoReadEnabled,
    pending: prev?.autoReadPendingSync,
  });
  return {
    patch: { autoReadEnabled: m.value, autoReadPendingSync: m.pending },
    push: m.needsPush ? { studentId: cloudStudent.id, localChildId: localChildId ?? null, value: m.value } : null,
  };
}

/**
 * Apply `transform` to the progress of `localChildId`: through the live setter
 * if that child is still active, otherwise directly in storage. Never throws
 * away another child's data: a deleted child loads as defaults, the transform
 * leaves those unchanged, and nothing is saved.
 * ctx: { getActiveChildId, setProgress, loadStored, saveStored }
 */
export function applyToChild(ctx, localChildId, transform) {
  const active = ctx.getActiveChildId ? ctx.getActiveChildId() : null;
  if (active === localChildId) {
    // Id-scoped live setter (RED R3-S1): saves under THAT child's key and falls
    // back to storage itself if the active child changed in the meantime.
    (ctx.setProgressForChild ? (fn) => ctx.setProgressForChild(localChildId, fn) : ctx.setProgress)(transform);
    return "live";
  }
  if (!localChildId) return "dropped";
  const stored = ctx.loadStored(localChildId);
  const next = transform(stored);
  if (next !== stored) ctx.saveStored(localChildId, next);
  return "stored";
}

/** Only PGRST116 (`.single()` matched 0 rows: the row is not ours / gone) is
 *  treated as permanent. postgrest-js errors carry no HTTP status, 42501 is
 *  also what a lost session (anon role) produces, and a missing column is
 *  already handled inside updateStudent (RED R2-S2). */
export function isPermanentWriteError(e) {
  return String(e?.code ?? "") === "PGRST116";
}

/** Permanent rejection: put `target` back, but only if the stored value is
 *  still the one that was rejected (a pull-merge may have rewritten it). */
const revertIfCurrent = (sent, target) => (prev) =>
  prev && prev.autoReadEnabled === sent
    ? { ...prev, autoReadEnabled: target.value ?? null, autoReadPendingSync: target.pending === true }
    : prev;

/**
 * Pure: is this child still linked to this cloud student for the SAME signed-in
 * user that started the write? Guards queued reconnect writes (RED R2-S4/R3-S3).
 * registry: { children: [{ id, supabaseStudentId }] }
 */
export function isChildLinkedFor({ registry, user, childId, studentId, userIdAtStart }) {
  if (!user) return false;
  if (userIdAtStart != null && user.id !== userIdAtStart) return false;
  return !!registry?.children?.some((c) => c.id === childId && c.supabaseStudentId === studentId);
}

/**
 * Pure (RED R4-S1): the reducer behind App's id-scoped setProgressForChild.
 * If `id` is still the live active child, transform the in-memory state and
 * save it under `id`. Otherwise transform THAT child's stored progress, save
 * it, and leave the in-memory (other child's) state untouched.
 */
export function progressUpdateForChild({ prev, liveId, id, fn, load, save }) {
  if (liveId !== id) {
    const stored = load(id);
    const out = fn(stored);
    if (out !== stored) save(id, out);
    return prev;
  }
  const next = fn(prev);
  if (next !== prev) save(id, next);
  return next;
}

// Last value the CLOUD is known to hold, per student (RED R3-S2). Set by a
// confirmed write and by the pull merge; reverts go back to it.
const confirmed = new Map();
export function noteAutoReadCloudValue(studentId, cloudValue) {
  if (!studentId || cloudValue === undefined) return; // absent column: unknown
  confirmed.set(studentId, { value: cloudValue });
}

// Per-student write counters (RED S3) and one-shot reconnect registrations.
const writeSeq = new Map();
const reconnects = new Map();
const seqOf = (studentId) => writeSeq.get(studentId) ?? 0;

function cancelReconnect(studentId) {
  const un = reconnects.get(studentId);
  if (un) { reconnects.delete(studentId); un(); }
}

async function runWrite({ studentId, localChildId, value, token, updateStudentFn, ctx, onError, onReconnect, oneShot, before }) {
  try {
    await updateStudentFn(studentId, { auto_read_enabled: value });
  } catch (e) {
    if (token !== seqOf(studentId)) return { ok: false, stale: true };
    const permanent = isPermanentWriteError(e);
    let reverted = false;
    if (permanent) {
      cancelReconnect(studentId);
      // Revert to the last value the cloud confirmed (synced => flag false);
      // failing that, to the pre-toggle value; failing that, just drop the
      // flag (cloud wins next load) and report reverted:false.
      const c = confirmed.get(studentId);
      const target = c ? { value: c.value, pending: false } : before ?? null;
      if (target) {
        applyToChild(ctx, localChildId, revertIfCurrent(value, target));
        reverted = true;
      } else {
        applyToChild(ctx, localChildId, clearIfCurrent(value));
      }
    } else if (onReconnect && !oneShot) {
      // Always (re)register for the LATEST failed write (RED R2-M1).
      cancelReconnect(studentId);
      const un = onReconnect(() => {
        cancelReconnect(studentId);
        if (token !== seqOf(studentId)) return; // a newer toggle owns it now
        // The child may have been removed / the user changed since (RED R2-S4).
        if (ctx.isChildLinked && !ctx.isChildLinked(localChildId, studentId)) return;
        void runWrite({ studentId, localChildId, value, token, updateStudentFn, ctx, onError, onReconnect, oneShot: true, before }).catch(() => {});
      });
      reconnects.set(studentId, un);
    }
    if (onError) onError(e, { permanent, reverted, retry: !!oneShot });
    return { ok: false, permanent, reverted };
  }
  if (token === seqOf(studentId)) {
    cancelReconnect(studentId);
    confirmed.set(studentId, { value });
    applyToChild(ctx, localChildId, clearIfCurrent(value));
    return { ok: true };
  }
  applyToChild(ctx, localChildId, rearm());
  return { ok: true, stale: true };
}

/**
 * The toggle's write logic. Local first (synchronous), then the cloud.
 * The pending flag is ALWAYS raised, also for an unlinked child (RED S6): the
 * first merge after the child is linked/created pushes the local value.
 * @returns {Promise<{ ok: boolean, stale?: boolean, permanent?: boolean, deferred?: boolean }>}
 */
export async function applyAutoReadToggle({ next, studentId, localChildId, ctx, updateStudentFn, onError, onReconnect }) {
  const before = { value: undefined, pending: false };
  ctx.setProgress((prev) => {
    before.value = prev?.autoReadEnabled;
    before.pending = prev?.autoReadPendingSync === true;
    return { ...prev, autoReadEnabled: next, autoReadPendingSync: true };
  });
  if (!studentId) return { ok: true, deferred: true };
  // A toggle made while synced (flag false) starts from what the cloud holds.
  if (!confirmed.has(studentId) && !before.pending) confirmed.set(studentId, { value: before.value ?? null });
  const token = seqOf(studentId) + 1;
  writeSeq.set(studentId, token);
  return runWrite({ studentId, localChildId, value: next, token, updateStudentFn, ctx, onError, onReconnect, before });
}

/**
 * Load-time retry for a flag a previous session couldn't clear.
 * `push` is planAutoReadOnMerge's push request.
 */
export async function syncPendingAutoRead({ push, ctx, updateStudentFn, onError, onReconnect }) {
  if (!push || !push.studentId || typeof push.value !== "boolean") return { ok: true };
  const token = seqOf(push.studentId);
  return runWrite({
    studentId: push.studentId,
    localChildId: push.localChildId,
    value: push.value,
    token,
    updateStudentFn,
    ctx,
    onError,
    onReconnect,
  });
}

/** TEST-ONLY (never call from product code): reset module counters between scenarios. */
export function __resetAutoReadSyncStateForTests() {
  for (const un of reconnects.values()) un();
  reconnects.clear();
  writeSeq.clear();
  confirmed.clear();
}
